import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Users, LogOut, Plus, AlertCircle, Settings, PawPrint, Camera, 
  Edit, ArrowRightLeft, Phone, Mail, Stethoscope, CheckCircle,
  Loader2, AlertTriangle, Bell
} from 'lucide-react';
import PrivacySettings from './PrivacySettings';
import { NotificationBell, NotificationsPanel } from '@/components/NotificationBell';

const OwnerDashboard = ({ user, onLogout }) => {
  const { t } = useTranslation();
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [missingDialogOpen, setMissingDialogOpen] = useState(false);
  const [selectedPet, setSelectedPet] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Enrollment form
  const [enrollForm, setEnrollForm] = useState({
    name: '', species: 'Dog', breed: '', color: '', gender: '',
    date_of_birth: '', spay_neuter_status: 'unknown', microchip_number: '',
    owner_name: user?.name || '', owner_email: user?.email || '',
    owner_phone_1: '', owner_phone_2: '',
    vet_name: '', vet_clinic: '', vet_phone: '', vet_email: '',
    photos: []
  });
  
  // Edit form
  const [editForm, setEditForm] = useState({});
  
  // Transfer form
  const [transferForm, setTransferForm] = useState({
    new_owner_name: '', new_owner_email: '', new_owner_phone: ''
  });
  
  // Missing report form
  const [missingForm, setMissingForm] = useState({
    missing_date: '', last_seen_location: '', description: '', reward: ''
  });

  useEffect(() => {
    fetchPets();
  }, []);

  const fetchPets = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/pets');
      setPets(response.data);
    } catch (error) {
      console.error('Failed to fetch pets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnrollPet = async (e) => {
    e.preventDefault();
    if (!enrollForm.name || !enrollForm.species) {
      toast.error('Please fill in pet name and species');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post('/pets/enroll', {
        ...enrollForm,
        host_url: window.location.origin
      });
      
      if (response.data.payment_url) {
        toast.success('Redirecting to payment...');
        window.location.href = response.data.payment_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enroll pet');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdatePet = async (e) => {
    e.preventDefault();
    if (!selectedPet) return;
    
    setSubmitting(true);
    try {
      await axios.put(`/pets/${selectedPet.pet_id}/update`, editForm);
      toast.success('Pet information updated!');
      setEditDialogOpen(false);
      fetchPets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update pet');
    } finally {
      setSubmitting(false);
    }
  };

  const [mailtoLink, setMailtoLink] = useState(null);
  const [transferSuccess, setTransferSuccess] = useState(false);
  const [acceptLink, setAcceptLink] = useState('');

  const handleTransferOwnership = async (e) => {
    e.preventDefault();
    if (!selectedPet) return;
    
    if (!transferForm.new_owner_name || !transferForm.new_owner_email) {
      toast.error('Please fill in new owner name and email');
      return;
    }
    
    setSubmitting(true);
    try {
      // Create the transfer record in backend (without sending email)
      const response = await axios.post(`/pets/${selectedPet.pet_id}/transfer`, {
        ...transferForm,
        host_url: window.location.origin,
        skip_email: true
      });
      
      const transferId = response.data.transfer_id;
      const link = `${window.location.origin}/accept-transfer/${transferId}`;
      setAcceptLink(link);
      
      // Create mailto link
      const subject = encodeURIComponent(`Pet Ownership Transfer - ${selectedPet.name}`);
      const body = encodeURIComponent(
`Hi ${transferForm.new_owner_name},

I would like to transfer ownership of my pet "${selectedPet.name}" to you.

Pet Details:
- Name: ${selectedPet.name}
- Species: ${selectedPet.species || 'N/A'}
- Breed: ${selectedPet.breed || 'N/A'}
- Microchip: ${selectedPet.microchip_number || 'N/A'}

To accept this transfer and become the registered owner, please click the link below:

${link}

You will need to pay a one-time $20 transfer fee to complete the registration.

Thank you!
${user.name}`
      );
      
      setMailtoLink(`mailto:${transferForm.new_owner_email}?subject=${subject}&body=${body}`);
      setTransferSuccess(true);
      toast.success('Transfer created! Now send the email to the new owner.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate transfer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseTransferDialog = () => {
    setTransferDialogOpen(false);
    setTransferSuccess(false);
    setMailtoLink(null);
    setAcceptLink('');
    setTransferForm({ new_owner_name: '', new_owner_email: '', new_owner_phone: '' });
  };

  const handleReportMissing = async (e) => {
    e.preventDefault();
    if (!selectedPet) return;
    
    setSubmitting(true);
    try {
      await axios.post(`/pets/${selectedPet.pet_id}/report-missing`, missingForm);
      toast.success('Pet reported as missing. Alert has been created.');
      setMissingDialogOpen(false);
      fetchPets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to report missing');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkFound = async (pet) => {
    try {
      await axios.post(`/pets/${pet.pet_id}/found`);
      toast.success('Pet marked as found!');
      fetchPets();
    } catch (error) {
      toast.error('Failed to update pet status');
    }
  };

  const openEditDialog = (pet) => {
    setSelectedPet(pet);
    setEditForm({
      name: pet.name || '',
      species: pet.species || 'Dog',
      breed: pet.breed || '',
      color: pet.color || '',
      gender: pet.gender || '',
      date_of_birth: pet.date_of_birth || '',
      spay_neuter_status: pet.spay_neuter_status || 'unknown',
      owner_name: pet.owner_name || user?.name || '',
      owner_email: pet.owner_email || user?.email || '',
      owner_phone_1: pet.owner_phone_1 || '',
      owner_phone_2: pet.owner_phone_2 || '',
      vet_name: pet.vet_name || '',
      vet_clinic: pet.vet_clinic || '',
      vet_phone: pet.vet_phone || '',
      vet_email: pet.vet_email || '',
      medical_notes: pet.medical_notes || ''
    });
    setEditDialogOpen(true);
  };

  const openTransferDialog = (pet) => {
    setSelectedPet(pet);
    setTransferForm({ new_owner_name: '', new_owner_email: '', new_owner_phone: '' });
    setTransferDialogOpen(true);
  };

  const openMissingDialog = (pet) => {
    setSelectedPet(pet);
    setMissingForm({
      missing_date: new Date().toISOString().split('T')[0],
      last_seen_location: '',
      description: '',
      reward: ''
    });
    setMissingDialogOpen(true);
  };

  const resetEnrollForm = () => {
    setEnrollForm({
      name: '', species: 'Dog', breed: '', color: '', gender: '',
      date_of_birth: '', spay_neuter_status: 'unknown', microchip_number: '',
      owner_name: user?.name || '', owner_email: user?.email || '',
      owner_phone_1: '', owner_phone_2: '',
      vet_name: '', vet_clinic: '', vet_phone: '', vet_email: '',
      photos: []
    });
  };

  return (
    <div className="min-h-screen bg-[#fdfbf7]">
      <header data-testid="owner-dashboard" className="bg-white border-b border-stone-100">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <PawPrint className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>My Pets</h1>
              <p className="text-sm text-stone-600">{user.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              onClick={() => { resetEnrollForm(); setEnrollDialogOpen(true); }}
              className="bg-primary hover:bg-primary/90"
            >
              <Plus className="w-4 h-4 mr-2" />
              Enroll New Pet
            </Button>
            <NotificationBell />
            <Button data-testid="logout-btn" onClick={onLogout} variant="outline" className="rounded-full">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        <Tabs defaultValue="pets" className="space-y-6">
          <TabsList>
            <TabsTrigger value="pets">My Pets</TabsTrigger>
            <TabsTrigger value="notifications">
              <Bell className="w-4 h-4 mr-2" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="privacy">
              <Settings className="w-4 h-4 mr-2" />
              Privacy & Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pets">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : pets.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <PawPrint className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No Pets Registered</h3>
                  <p className="text-stone-600 mb-6">Enroll your pet to access microchip registration, missing alerts, and more.</p>
                  <Button onClick={() => { resetEnrollForm(); setEnrollDialogOpen(true); }} className="bg-primary">
                    <Plus className="w-4 h-4 mr-2" />
                    Enroll Your First Pet
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {pets.map((pet) => (
                  <Card key={pet.pet_id} className={`hover:shadow-md transition-shadow ${pet.status === 'lost' ? 'border-red-300' : ''}`}>
                    <CardContent className="pt-6">
                      {/* Pet Photo & Status */}
                      <div className="flex items-start gap-4 mb-4">
                        <div className="w-20 h-20 rounded-lg bg-stone-100 flex items-center justify-center overflow-hidden">
                          {pet.photos?.length > 0 ? (
                            <img src={pet.photos[0]} alt={pet.name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-3xl">
                              {pet.species === 'Dog' ? '🐕' : pet.species === 'Cat' ? '🐈' : '🐾'}
                            </span>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="text-xl font-bold">{pet.name}</h3>
                            {pet.status === 'lost' && (
                              <Badge variant="destructive" className="text-xs">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                MISSING
                              </Badge>
                            )}
                            {pet.registration_status === 'registered' && (
                              <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Registered
                              </Badge>
                            )}
                          </div>
                          <p className="text-stone-600">{pet.species} • {pet.breed || 'Unknown breed'}</p>
                          <p className="text-sm text-stone-500">{pet.gender} • {pet.color}</p>
                        </div>
                      </div>

                      {/* Microchip */}
                      {pet.microchip_number && (
                        <div className="bg-stone-50 p-3 rounded-lg mb-4">
                          <p className="text-xs text-stone-500 uppercase tracking-wide">Microchip</p>
                          <p className="font-mono font-semibold">{pet.microchip_number}</p>
                        </div>
                      )}

                      {/* Owner Contact */}
                      <div className="space-y-2 mb-4 text-sm">
                        {pet.owner_phone_1 && (
                          <div className="flex items-center gap-2 text-stone-600">
                            <Phone className="w-4 h-4" />
                            <span>{pet.owner_phone_1}</span>
                            {pet.owner_phone_2 && <span>• {pet.owner_phone_2}</span>}
                          </div>
                        )}
                        {pet.vet_name && (
                          <div className="flex items-center gap-2 text-stone-600">
                            <Stethoscope className="w-4 h-4" />
                            <span>{pet.vet_name} {pet.vet_clinic && `- ${pet.vet_clinic}`}</span>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={() => openEditDialog(pet)}>
                          <Edit className="w-3 h-3 mr-1" /> Edit Info
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openTransferDialog(pet)}>
                          <ArrowRightLeft className="w-3 h-3 mr-1" /> Transfer
                        </Button>
                        {pet.status === 'lost' ? (
                          <Button 
                            size="sm" 
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleMarkFound(pet)}
                          >
                            <CheckCircle className="w-3 h-3 mr-1" /> Mark Found
                          </Button>
                        ) : (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            onClick={() => openMissingDialog(pet)}
                          >
                            <AlertCircle className="w-3 h-3 mr-1" /> Report Missing
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="notifications">
            <NotificationsPanel />
          </TabsContent>

          <TabsContent value="privacy">
            <PrivacySettings user={user} />
          </TabsContent>
        </Tabs>

        {/* ============ ENROLL PET DIALOG ============ */}
        <Dialog open={enrollDialogOpen} onOpenChange={setEnrollDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5" />
                Enroll New Pet
              </DialogTitle>
              <CardDescription>
                Register your pet for lifetime protection. Fee: $20 (one-time)
              </CardDescription>
            </DialogHeader>
            
            <form onSubmit={handleEnrollPet} className="space-y-6">
              {/* Pet Information */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <PawPrint className="w-4 h-4" /> Pet Information
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Pet Name *</Label>
                    <Input
                      value={enrollForm.name}
                      onChange={(e) => setEnrollForm({...enrollForm, name: e.target.value})}
                      placeholder="e.g., Buddy"
                      required
                    />
                  </div>
                  <div>
                    <Label>Species *</Label>
                    <Select value={enrollForm.species} onValueChange={(v) => setEnrollForm({...enrollForm, species: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Dog">Dog</SelectItem>
                        <SelectItem value="Cat">Cat</SelectItem>
                        <SelectItem value="Bird">Bird</SelectItem>
                        <SelectItem value="Rabbit">Rabbit</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Breed</Label>
                    <Input
                      value={enrollForm.breed}
                      onChange={(e) => setEnrollForm({...enrollForm, breed: e.target.value})}
                      placeholder="e.g., Golden Retriever"
                    />
                  </div>
                  <div>
                    <Label>Color</Label>
                    <Input
                      value={enrollForm.color}
                      onChange={(e) => setEnrollForm({...enrollForm, color: e.target.value})}
                      placeholder="e.g., Golden"
                    />
                  </div>
                  <div>
                    <Label>Gender</Label>
                    <Select value={enrollForm.gender} onValueChange={(v) => setEnrollForm({...enrollForm, gender: v})}>
                      <SelectTrigger><SelectValue placeholder="Select gender" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                        <SelectItem value="Unknown">Unknown</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Date of Birth</Label>
                    <Input
                      type="date"
                      value={enrollForm.date_of_birth}
                      onChange={(e) => setEnrollForm({...enrollForm, date_of_birth: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Spay/Neuter Status</Label>
                    <Select value={enrollForm.spay_neuter_status} onValueChange={(v) => setEnrollForm({...enrollForm, spay_neuter_status: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unknown">Unknown</SelectItem>
                        <SelectItem value="spayed">Spayed</SelectItem>
                        <SelectItem value="neutered">Neutered</SelectItem>
                        <SelectItem value="intact">Intact</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Microchip Number</Label>
                    <Input
                      value={enrollForm.microchip_number}
                      onChange={(e) => setEnrollForm({...enrollForm, microchip_number: e.target.value})}
                      placeholder="15-digit number"
                    />
                  </div>
                </div>
              </div>

              {/* Owner Contact */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" /> Owner Contact Information
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Owner Name *</Label>
                    <Input
                      value={enrollForm.owner_name}
                      onChange={(e) => setEnrollForm({...enrollForm, owner_name: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={enrollForm.owner_email}
                      onChange={(e) => setEnrollForm({...enrollForm, owner_email: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>Phone 1</Label>
                    <Input
                      value={enrollForm.owner_phone_1}
                      onChange={(e) => setEnrollForm({...enrollForm, owner_phone_1: e.target.value})}
                      placeholder="Primary phone"
                    />
                  </div>
                  <div>
                    <Label>Phone 2</Label>
                    <Input
                      value={enrollForm.owner_phone_2}
                      onChange={(e) => setEnrollForm({...enrollForm, owner_phone_2: e.target.value})}
                      placeholder="Secondary phone"
                    />
                  </div>
                </div>
              </div>

              {/* Vet Information */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Stethoscope className="w-4 h-4" /> Veterinarian Information
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Vet Name</Label>
                    <Input
                      value={enrollForm.vet_name}
                      onChange={(e) => setEnrollForm({...enrollForm, vet_name: e.target.value})}
                      placeholder="Dr. Smith"
                    />
                  </div>
                  <div>
                    <Label>Clinic Name</Label>
                    <Input
                      value={enrollForm.vet_clinic}
                      onChange={(e) => setEnrollForm({...enrollForm, vet_clinic: e.target.value})}
                      placeholder="Happy Paws Clinic"
                    />
                  </div>
                  <div>
                    <Label>Vet Phone</Label>
                    <Input
                      value={enrollForm.vet_phone}
                      onChange={(e) => setEnrollForm({...enrollForm, vet_phone: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Vet Email</Label>
                    <Input
                      type="email"
                      value={enrollForm.vet_email}
                      onChange={(e) => setEnrollForm({...enrollForm, vet_email: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-green-800">
                  <strong>Registration Fee: $20.00</strong> (Lifetime - One time payment)
                </p>
                <p className="text-xs text-green-600 mt-1">
                  Includes: Microchip registration, missing pet alerts, ownership records, and more.
                </p>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEnrollDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} className="bg-primary">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Proceed to Payment ($20)
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* ============ EDIT PET DIALOG ============ */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="w-5 h-5" />
                Edit Pet Information - {selectedPet?.name}
              </DialogTitle>
              <CardDescription>
                Update your pet and contact information. Microchip number cannot be changed.
              </CardDescription>
            </DialogHeader>
            
            <form onSubmit={handleUpdatePet} className="space-y-6">
              {/* Show microchip as read-only */}
              {selectedPet?.microchip_number && (
                <div className="bg-stone-100 p-3 rounded-lg">
                  <Label className="text-xs text-stone-500">Microchip Number (Cannot be changed)</Label>
                  <p className="font-mono font-semibold">{selectedPet.microchip_number}</p>
                </div>
              )}

              {/* Pet Information */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Pet Name</Label>
                  <Input
                    value={editForm.name}
                    onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Species</Label>
                  <Select value={editForm.species} onValueChange={(v) => setEditForm({...editForm, species: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Dog">Dog</SelectItem>
                      <SelectItem value="Cat">Cat</SelectItem>
                      <SelectItem value="Bird">Bird</SelectItem>
                      <SelectItem value="Rabbit">Rabbit</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Breed</Label>
                  <Input
                    value={editForm.breed}
                    onChange={(e) => setEditForm({...editForm, breed: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Color</Label>
                  <Input
                    value={editForm.color}
                    onChange={(e) => setEditForm({...editForm, color: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Gender</Label>
                  <Select value={editForm.gender} onValueChange={(v) => setEditForm({...editForm, gender: v})}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                      <SelectItem value="Unknown">Unknown</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Spay/Neuter Status</Label>
                  <Select value={editForm.spay_neuter_status} onValueChange={(v) => setEditForm({...editForm, spay_neuter_status: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unknown">Unknown</SelectItem>
                      <SelectItem value="spayed">Spayed</SelectItem>
                      <SelectItem value="neutered">Neutered</SelectItem>
                      <SelectItem value="intact">Intact</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Owner Contact */}
              <div>
                <h4 className="font-semibold mb-3">Owner Contact</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Owner Name</Label>
                    <Input
                      value={editForm.owner_name}
                      onChange={(e) => setEditForm({...editForm, owner_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={editForm.owner_email}
                      onChange={(e) => setEditForm({...editForm, owner_email: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Phone 1</Label>
                    <Input
                      value={editForm.owner_phone_1}
                      onChange={(e) => setEditForm({...editForm, owner_phone_1: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Phone 2</Label>
                    <Input
                      value={editForm.owner_phone_2}
                      onChange={(e) => setEditForm({...editForm, owner_phone_2: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* Vet Information */}
              <div>
                <h4 className="font-semibold mb-3">Veterinarian</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Vet Name</Label>
                    <Input
                      value={editForm.vet_name}
                      onChange={(e) => setEditForm({...editForm, vet_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Clinic</Label>
                    <Input
                      value={editForm.vet_clinic}
                      onChange={(e) => setEditForm({...editForm, vet_clinic: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Vet Phone</Label>
                    <Input
                      value={editForm.vet_phone}
                      onChange={(e) => setEditForm({...editForm, vet_phone: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Vet Email</Label>
                    <Input
                      type="email"
                      value={editForm.vet_email}
                      onChange={(e) => setEditForm({...editForm, vet_email: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label>Medical Notes</Label>
                <Textarea
                  value={editForm.medical_notes}
                  onChange={(e) => setEditForm({...editForm, medical_notes: e.target.value})}
                  placeholder="Any medical conditions, allergies, etc."
                  rows={3}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save Changes
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* ============ TRANSFER OWNERSHIP DIALOG ============ */}
        <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ArrowRightLeft className="w-5 h-5" />
                Transfer Ownership - {selectedPet?.name}
              </DialogTitle>
              <CardDescription>
                Transfer this pet to a new owner
              </CardDescription>
            </DialogHeader>
            
            {!transferSuccess ? (
              <form onSubmit={handleTransferOwnership} className="space-y-4">
                <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
                  <Mail className="w-4 h-4 inline mr-2" />
                  After submitting, you will get a link to send via email.
                </div>

                <div>
                  <Label>New Owner Name *</Label>
                  <Input
                    value={transferForm.new_owner_name}
                    onChange={(e) => setTransferForm({...transferForm, new_owner_name: e.target.value})}
                    placeholder="Full name"
                    required
                  />
                </div>
                <div>
                  <Label>New Owner Email *</Label>
                  <Input
                    type="email"
                    value={transferForm.new_owner_email}
                    onChange={(e) => setTransferForm({...transferForm, new_owner_email: e.target.value})}
                    placeholder="email@example.com"
                    required
                  />
                </div>
                <div>
                  <Label>New Owner Phone</Label>
                  <Input
                    value={transferForm.new_owner_phone}
                    onChange={(e) => setTransferForm({...transferForm, new_owner_phone: e.target.value})}
                    placeholder="Phone number"
                  />
                </div>

                <div className="bg-stone-50 p-3 rounded-lg text-sm">
                  <p><strong>Transfer Fee:</strong> $20.00 (paid by new owner)</p>
                  <p className="text-stone-600 mt-1">
                    The new owner will click a link to accept and pay the $20 fee.
                  </p>
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={handleCloseTransferDialog}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Create Transfer
                  </Button>
                </DialogFooter>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                  <p className="font-semibold text-green-800">Transfer Created!</p>
                  <p className="text-sm text-green-600">Now send the link to {transferForm.new_owner_email}</p>
                </div>

                <div className="space-y-3">
                  <p className="text-sm font-medium">Option 1: Click to open your email</p>
                  <a 
                    href={mailtoLink} 
                    className="block w-full bg-blue-600 hover:bg-blue-700 text-white text-center py-3 rounded-lg font-medium"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Mail className="w-4 h-4 inline mr-2" />
                    Open Email App
                  </a>
                </div>

                <div className="space-y-3">
                  <p className="text-sm font-medium">Option 2: Copy the link and send manually</p>
                  <div className="flex gap-2">
                    <Input value={acceptLink} readOnly className="text-xs" />
                    <Button 
                      type="button"
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(acceptLink);
                        toast.success('Link copied to clipboard!');
                      }}
                    >
                      Copy
                    </Button>
                  </div>
                  <p className="text-xs text-stone-500">
                    Send this link to {transferForm.new_owner_email}. They will pay $20 to complete the transfer.
                  </p>
                </div>

                <DialogFooter>
                  <Button type="button" onClick={handleCloseTransferDialog}>
                    Done
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* ============ REPORT MISSING DIALOG ============ */}
        <Dialog open={missingDialogOpen} onOpenChange={setMissingDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Report Missing - {selectedPet?.name}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleReportMissing} className="space-y-4">
              <div className="bg-red-50 p-3 rounded-lg text-sm text-red-800">
                This will create a public missing pet alert. Anyone searching this microchip will be notified that the pet is missing.
              </div>

              <div>
                <Label>When did they go missing? *</Label>
                <Input
                  type="date"
                  value={missingForm.missing_date}
                  onChange={(e) => setMissingForm({...missingForm, missing_date: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Last Seen Location *</Label>
                <Input
                  value={missingForm.last_seen_location}
                  onChange={(e) => setMissingForm({...missingForm, last_seen_location: e.target.value})}
                  placeholder="123 Main St, City, State"
                  required
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={missingForm.description}
                  onChange={(e) => setMissingForm({...missingForm, description: e.target.value})}
                  placeholder="Any distinguishing features, collar color, behavior..."
                  rows={3}
                />
              </div>
              <div>
                <Label>Reward (Optional)</Label>
                <Input
                  type="number"
                  value={missingForm.reward}
                  onChange={(e) => setMissingForm({...missingForm, reward: e.target.value})}
                  placeholder="0.00"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setMissingDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} className="bg-red-600 hover:bg-red-700">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Report as Missing
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default OwnerDashboard;
